import { Service, inject } from '@angular/core';
import { Params } from '@angular/router';
import { HttpBasicRequest } from 'app/shared/models/http-basic-request.model';
import { PagedResponse } from 'app/shared/models/paged-response.model';
import { BasicHttpClientService } from 'app/shared/services/basic-http-client.service';
import { environment } from 'environments/environment';
import { observable, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { VoucherDto, VoucherPrivilageCashDto, VoucherVM } from '../../models/back-office-admin-common/vouchers.model';
import { CustomerVM } from '../../models/customer/customer.model';
import { VoucherStatusEnum } from 'app/shared/enums/voucher-status.enum';

@Service()
export class VouchersService {
  private _httpClient = inject(BasicHttpClientService);

  private _baseUrl = environment.urls.apiUrl.url;

  public getVouchers(loaderId: string = null, skipCount: number, maxResultCount: number, storeId: number, searchString: string = '', customerId: number = null, status: VoucherStatusEnum = VoucherStatusEnum.OnlyNotArchived): Observable<PagedResponse<VoucherVM>> {
    const params = {
      skipCount: skipCount.toString(),
      maxResultCount: maxResultCount.toString(),
      searchString,
      storeId,
      status
    };

    if (customerId) params['customerId'] = customerId;

    const url = this._baseUrl + '/vouchers';

    return this._httpClient.get<PagedResponse<VoucherDto>>(url, {
      params
    }, loaderId).pipe(map(x => new PagedResponse({
      items: x.items.map(l => new VoucherVM(l)),
      totalCount: x.totalCount
    })));
  }

  public getVoucher(loaderId: string, voucherId: number) {
    const url = this._baseUrl + '/vouchers/' + voucherId;

    return this._httpClient.get<VoucherVM>(url, null, loaderId);
  }

  public getPrivilageCash(loaderId: string, customerId: number, storeId: number): Observable<VoucherPrivilageCashDto[]> {
    return this._httpClient.get(
      `${this._baseUrl}/vouchers/list-available-cash-privileges`,
      { params: { customerId, storeId } },
      loaderId
    );
  }

  public createVoucher(loaderId: string, voucher: VoucherVM, selectedCustomers: CustomerVM[]) {
    const params: Params = {
      code: voucher.code,
      externalId: voucher.externalId ?? null,
      description: voucher.description,
      storeId: voucher.storeId,
      validityDateFrom: voucher.validityDateFrom,
      validityDateTo: voucher.validityDateTo,
      amount: voucher.amount,
      threshold: voucher.threshold ?? null,
      constraintType: voucher.constraintType,
      applicableAsPartialPayment: voucher.applicableAsPartialPayment ?? false,
    };
    const result = new Subject();
    this._httpClient.post(`${this._baseUrl}/vouchers`, params, {}, loaderId).subscribe((res: VoucherVM) => {
      const requests: HttpBasicRequest[] = selectedCustomers.map(x => new HttpBasicRequest({ url: `${this._baseUrl}/customers/${x.id}/voucher/${res.id}`, method: 'post' }));
      this._httpClient.forkJoin(requests, loaderId).subscribe(() => {
        result.next(undefined);
      });
    });

    return result;
  }

  public saveVoucher(loaderId: string, voucher: VoucherVM): Observable<any> {
    const result = new Subject();
    const url = this._baseUrl + '/vouchers/' + voucher.id;
    this._httpClient.put<VoucherVM>(url, voucher, {}, loaderId).subscribe((res) => {
      result.next(res);
    });
    return result;
  }

  public addVoucherToCustomer(loaderId: string, customerId: number, voucherId: number) {
    const url = `${this._baseUrl}/customers/${customerId}/voucher/${voucherId}`;
    return this._httpClient.post(url, null, {}, loaderId);
  }

  public deleteVoucher(voucherId: number, loaderId: string) {
    const url = `${this._baseUrl}/vouchers/${voucherId}`;
    return this._httpClient.delete(url, null, loaderId);
  }

  public removeVoucherFromCustomer(loaderId: string, customerId: number, voucherId: number) {
    const url = `${this._baseUrl}/customers/${customerId}/voucher/${voucherId}`;
    return this._httpClient.delete(url, null, loaderId);
  }

  public archiveVoucher(loaderId: string, voucher: VoucherVM) {
    return this.saveVoucher(loaderId, { ...voucher, isArchived: true } as VoucherVM);
  }

}
